import React, { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Tooltip from '@mui/material/Tooltip';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ArrowBack from '@mui/icons-material/ArrowBack';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import { useNavigate } from 'react-router-dom';

import { getMeCached } from '../authCache';
import { fetchClasses, fetchCriteria, createCriteria, deleteCriteria, updateCriteria } from './api/starGardenApi';

const FLOWERS = ['🌼', '🌷', '🌻', '🌸', '🪻', '🌺'];

const CriteriaManagementPage = () => {
  const navigate = useNavigate();

  const [schoolId, setSchoolId] = useState(null);

  const [classes, setClasses] = useState([]);
  const [selectedClassId, setSelectedClassId] = useState('');

  const [criteria, setCriteria] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Delete confirmation modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deleteRow, setDeleteRow] = useState(null);

  // Add form
  const [newName, setNewName] = useState('');
  const [newSort, setNewSort] = useState('99');

  // Edit row
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [editSort, setEditSort] = useState('');

  const selectedClassName = useMemo(() => {
    if (!selectedClassId) return 'Sınıf seçin';
    const found = classes.find((c) => String(c.id) === String(selectedClassId));
    return found?.name || String(selectedClassId);
  }, [classes, selectedClassId]);

  const classNameById = useMemo(() => {
    const map = {};
    (classes || []).forEach((c) => {
      map[String(c.id)] = c.name;
    });
    return map;
  }, [classes]);

  // Load session + classes
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const me = await getMeCached();
        if (cancelled) return;
        const user = me?.data?.admin_user;
        if (!me?.ok || !user?.school_id) {
          setError('Oturum bilgisi alınamadı. Lütfen tekrar giriş yapın.');
          setLoading(false);
          return;
        }

        const userRole = String(user.role || '').toLowerCase();

        // Requirement: admin can manage criteria.
        if (userRole !== 'admin' && userRole !== 'superadmin') {
          setError('Bu sayfaya sadece admin erişebilir.');
          setLoading(false);
          return;
        }

        setSchoolId(user.school_id);

        const list = await fetchClasses(user.school_id);
        if (cancelled) return;
        const sorted = (list || []).sort((a, b) => (a.name || '').localeCompare(b.name || '', 'tr-TR'));
        setClasses(sorted);
        if (sorted.length) setSelectedClassId(String(sorted[0].id));
      } catch (e) {
        if (!cancelled) setError('Veriler yüklenemedi.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadCriteria = useCallback(async () => {
    if (!schoolId || !selectedClassId) return;
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetchCriteria(schoolId, Number(selectedClassId));
      const items = Array.isArray(res) ? res : (res?.items || []);
      setCriteria(items);
    } catch (e) {
      setError('Kriterler yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [schoolId, selectedClassId]);

  useEffect(() => {
    loadCriteria();
  }, [loadCriteria]);

  const handleAdd = useCallback(async () => {
    if (!schoolId || !selectedClassId) return;
    const name = String(newName || '').trim();
    if (!name) return;

    setError('');
    setSuccess('');
    try {
      const sortOrder = Number(newSort || 99);
      await createCriteria(schoolId, {
        classId: Number(selectedClassId),
        name,
        icon: null,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : 99,
      });
      setNewName('');
      setNewSort('99');
      setSuccess('Kriter eklendi.');
      await loadCriteria();
    } catch (e) {
      setError('Kriter eklenemedi: ' + (e?.message || ''));
    }
  }, [schoolId, selectedClassId, newName, newSort, loadCriteria]);

  const startEdit = (row) => {
    setEditingId(row.id);
    setEditName(row.name || '');
    setEditSort(String(row.sort_order ?? ''));
    setError('');
    setSuccess('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditName('');
    setEditSort('');
  };

  const saveEdit = useCallback(async () => {
    if (!schoolId || !editingId) return;
    const name = String(editName || '').trim();
    if (!name) {
      setError('Kriter adı boş olamaz.');
      return;
    }
    setError('');
    setSuccess('');
    try {
      const sortOrder = editSort === '' ? null : Number(editSort);
      await updateCriteria(schoolId, editingId, {
        name,
        sortOrder: Number.isFinite(sortOrder) ? sortOrder : null,
      });
      setSuccess('Kriter güncellendi.');
      cancelEdit();
      await loadCriteria();
    } catch (e) {
      setError('Kriter güncellenemedi: ' + (e?.message || ''));
    }
  }, [schoolId, editingId, editName, editSort, loadCriteria]);

  const requestDelete = useCallback((row) => {
    setError('');
    setSuccess('');
    setDeleteRow(row);
    setDeleteOpen(true);
  }, []);

  const closeDelete = useCallback(() => {
    setDeleteOpen(false);
    setDeleteRow(null);
  }, []);

  const confirmDelete = useCallback(async () => {
    if (!schoolId || !deleteRow) return;
    setError('');
    setSuccess('');
    try {
      await deleteCriteria(schoolId, deleteRow.id);
      setSuccess('Kriter silindi.');
      closeDelete();
      await loadCriteria();
    } catch (e) {
      setError('Kriter silinemedi: ' + (e?.message || ''));
      closeDelete();
    }
  }, [schoolId, deleteRow, loadCriteria, closeDelete]);

  return (
    <Box
      sx={{
        minHeight: '100dvh',
        bgcolor: '#e8f5e9',
        p: { xs: 2, md: 3 },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate('/admin/stargarden')}>
          <ArrowBack />
        </IconButton>
        <Typography variant="h5" sx={{ fontWeight: 800, color: '#2e7d32' }}>
          Kriter Düzenleme
        </Typography>
      </Box>

      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }} elevation={2}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>Sınıf</InputLabel>
            <Select
              label="Sınıf"
              value={selectedClassId}
              onChange={(e) => setSelectedClassId(String(e.target.value))}
            >
              {classes.map((c) => (
                <MenuItem key={c.id} value={String(c.id)}>{c.name}</MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Seçili kapsam: <b>{selectedClassName}</b>
          </Typography>
        </Box>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Paper sx={{ p: 2, borderRadius: 3, mb: 2 }} elevation={2}>
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Yeni Kriter Ekle
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
          <TextField
            size="small"
            label="Kriter adı"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            sx={{ minWidth: 260, flex: 1 }}
          />
          <TextField
            size="small"
            label="Sıra"
            value={newSort}
            onChange={(e) => setNewSort(e.target.value)}
            sx={{ width: 120 }}
          />
          <Button
            variant="contained"
            onClick={handleAdd}
            disabled={!selectedClassId || !String(newName || '').trim()}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Ekle
          </Button>
        </Box>
      </Paper>

      <Paper sx={{ borderRadius: 3 }} elevation={2}>
        <Box sx={{ p: 2, borderBottom: '1px solid rgba(0,0,0,0.08)' }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Kriterler
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Kriter silindiğinde geçmiş yıldızlar korunur; kriter yeni yıldız vermede görünmez.
          </Typography>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: '70vh' }}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700 }}>Çiçek</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Ad</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Kapsam</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Sıra</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Kullanım</TableCell>
                  <TableCell sx={{ fontWeight: 700 }}>Varsayılan</TableCell>
                  <TableCell sx={{ fontWeight: 700 }} align="right">İşlem</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {criteria.map((row, index) => {
                  const isEditing = editingId === row.id;
                  const scopeLabel = classNameById[String(row.class_id)] || `Sınıf ${row.class_id}`;

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell sx={{ width: 72 }}>
                        <Typography sx={{ fontSize: 22, lineHeight: 1 }}>{FLOWERS[index % FLOWERS.length]}</Typography>
                      </TableCell>

                      <TableCell>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            sx={{ minWidth: 240 }}
                          />
                        ) : (
                          row.name
                        )}
                      </TableCell>

                      <TableCell>{scopeLabel}</TableCell>

                      <TableCell sx={{ width: 90 }}>
                        {isEditing ? (
                          <TextField
                            size="small"
                            value={editSort}
                            onChange={(e) => setEditSort(e.target.value)}
                            sx={{ width: 80 }}
                          />
                        ) : (
                          row.sort_order
                        )}
                      </TableCell>

                      <TableCell sx={{ width: 90 }}>{row.usage_count ?? 0}</TableCell>
                      <TableCell sx={{ width: 110 }}>{row.is_default ? 'Evet' : 'Hayır'}</TableCell>

                      <TableCell align="right" sx={{ width: 170 }}>
                        {!isEditing ? (
                          <>
                            <Tooltip title="Düzenle">
                              <IconButton size="small" onClick={() => startEdit(row)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>

                            <Tooltip title="Sil">
                              <span>
                                <IconButton
                                  size="small"
                                  onClick={() => requestDelete(row)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </>
                        ) : (
                          <>
                            <Tooltip title="Kaydet">
                              <IconButton size="small" onClick={saveEdit}>
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="İptal">
                              <IconButton size="small" onClick={cancelEdit}>
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}

                {criteria.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography sx={{ color: 'text.secondary', py: 2 }}>
                        Kriter bulunamadı.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={deleteOpen} onClose={closeDelete} maxWidth="xs" fullWidth>
        <DialogTitle>Kriteri Sil</DialogTitle>
        <DialogContent>
          <Typography sx={{ mt: 1 }}>
            <b>{deleteRow?.name || ''}</b> kriteri silinsin mi?
          </Typography>
          <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
            Geçmiş yıldız kayıtları korunur.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={closeDelete} sx={{ textTransform: 'none' }}>
            Vazgeç
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={confirmDelete}
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Sil
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CriteriaManagementPage;
